import {
  Checkbox,
  Icon,
  KeyValueCustomMultipleSelect,
  KeyValueCustomSelect,
  RadioButton,
  Text,
  useSelectState,
} from "@smartsuite/react-ui/lib";
import type { InputGroupState } from "@smartsuite/react-ui/lib/components/forms/InputGroup/InputGroup.constants";
import type { CollisionInput } from "@smartsuite/react-ui/lib/components/utils/ElementPosition/ElementPosition.types";
import type { FieldChoice, FieldChoiceValue, MultipleSelectFieldValue } from "@smartsuite/types";
import cx from "classnames";
import type React from "react";
import { type FunctionComponent, useCallback, useEffect, useId, useState } from "react";
import { noop } from "rxjs";

import {
  OTHER_OPTION_VALUE,
  OtherOptionField,
} from "../../../../components/OtherOptionField/OtherOptionField";
import { toMultipleValue } from "../../../../helpers/multipleValue";

import "./ChoiceSelect.sass";

export type ChoiceSelectType = "dropdown" | "inline";

export interface ChoiceSelectProps {
  formState: InputGroupState;
  isMultipleSelect?: boolean;
  columnCount: number;
  type: ChoiceSelectType;
  options: FieldChoice[];
  value: FieldChoice["value"] | MultipleSelectFieldValue;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  allowSelection?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  showFilterGreaterThan?: number | boolean;
  collision?: CollisionInput;
  className?: string;
  onChange?: (value: FieldChoice["value"] | Array<FieldChoice["value"]>) => void;
  onChangeFocused?: (focused: boolean) => void;
  onOpenedChange?: (opened: boolean) => void;
}

const renderChoicePill = (
  option: FieldChoice,
  isSelected: boolean,
  isRadio: boolean,
  isRemovable: boolean,
  onChange?: (value: FieldChoice["value"] | Array<FieldChoice["value"]>) => void,
): JSX.Element => {
  const classes = cx("choice-select-dropdown__item", {
    "is-selected": isSelected,
    "is-radio": isRadio,
    "is-removable": isRemovable,
  });
  return (
    <>
      <div className={classes}>
        <div className="choice-select-dropdown__label">
          {option.label}
          {isRemovable && (
            <button
              className="choice-select-dropdown__remove"
              type="button"
              onClick={() => onChange?.(option.value)}
            >
              <Icon name="close-1px" size="S" color="currentColor" />
            </button>
          )}
        </div>
        {option.value_help_text && !isSelected && <Text size="xs">{option.value_help_text}</Text>}
      </div>
    </>
  );
};

const ChoiceSelectDropdown: FunctionComponent<ChoiceSelectProps> = ({
  formState,
  isMultipleSelect,
  options,
  value,
  onChange,
  onChangeFocused,
  disabled,
  readOnly,
  allowSelection,
  placeholder,
  searchPlaceholder,
  showFilterGreaterThan,
  collision,
}) => {
  const [state, onOpenedChange] = useSelectState(formState);

  // Staging state — multi-select dropdown commits only via Done.
  // toMultipleValue, not a bare cast: a restored checkpoint or prefill can hold the scalar shape
  // of a field that used to be single-valued (FORMS-WEB-X1).
  const externalMultiValue = isMultipleSelect ? toMultipleValue<string>(value) : [];
  const externalKey = externalMultiValue.map(String).join(",");
  const [draftValue, setDraftValue] = useState<MultipleSelectFieldValue>(externalMultiValue);

  // Reset draft whenever the dropdown is not active (closed) or external value changes.
  // This covers: outside-click, Esc, chevron click, and Cancel — all routed via state.
  useEffect(() => {
    if (state !== "active") {
      setDraftValue(externalMultiValue);
    }
  }, [state, externalKey]);

  // Per-chip remove — commits immediately, also clears the value from staged draft so the
  // open panel reflects the removal if the dropdown happens to be open.
  const handleRemove = useCallback(
    (removeValue: FieldChoiceValue | FieldChoiceValue[]) => {
      const removedValue = externalMultiValue.filter(
        (option) => String(option) !== String(removeValue),
      );
      setDraftValue((prev) => prev.filter((option) => String(option) !== String(removeValue)));
      onChange?.(removedValue);
    },
    [onChange, externalMultiValue],
  );

  // SDK MultipleSelect closes the dropdown internally on Done/Cancel; we only commit/discard.
  const handleDone = useCallback(() => {
    onChange?.(draftValue);
    onOpenedChange(false);
  }, [draftValue, onChange, onOpenedChange]);

  const handleCancel = useCallback(() => {
    setDraftValue(externalMultiValue);
    onOpenedChange(false);
  }, [externalMultiValue, onOpenedChange]);

  if (isMultipleSelect) {
    const isOpen = state === "active";
    // Panel reflects the staged draft; collapsed chips reflect the committed value.
    const displayedValue = isOpen ? draftValue : externalMultiValue;

    return (
      <KeyValueCustomMultipleSelect<"value", "label", FieldChoice>
        className="choice-select-dropdown-multiple"
        placeholderArrowStyle="arrow-down-smaller"
        selectListItemsClass="choice-select-dropdown__option"
        showClear={true}
        options={options}
        value={displayedValue}
        onChange={(next) => setDraftValue(next as MultipleSelectFieldValue)}
        onChangeFocused={onChangeFocused}
        disabled={disabled}
        isReadOnly={readOnly}
        state={state}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        collision={collision}
        onOpenedChange={onOpenedChange}
        selectListFooter={true}
        // onSave/onCancel are forwarded to the inner MultipleSelect, which calls them after
        // closing the dropdown via setIsOpened(false). KeyValueCustomMultipleSelectProps does
        // not declare these in its .d.ts but they pass through the spread.
        {...({
          onSave: handleDone,
          onCancel: handleCancel,
        } as unknown as Record<string, () => void>)}
        renderOption={(option) => renderChoicePill(option, false, false, false)}
        renderSelectedOption={(option) => renderChoicePill(option, true, false, true, handleRemove)}
        onClickOutside={handleDone}
      />
    );
  }

  return (
    <KeyValueCustomSelect<"value", "label", FieldChoice>
      listItemsClassname="choice-select-dropdown"
      clearIconName="close-thin"
      placeholderArrowStyle="arrow-down-smaller"
      showClear={true}
      options={options}
      value={value as FieldChoice["value"]}
      onChange={onChange}
      onChangeFocused={onChangeFocused}
      disabled={disabled}
      isReadOnly={readOnly}
      allowSelection={allowSelection ?? !readOnly}
      state={state}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      showFilterGreaterThan={showFilterGreaterThan as number}
      collision={collision}
      onOpenedChange={onOpenedChange}
      renderOption={(option) => renderChoicePill(option, false, false, false)}
      renderSelectedOption={(option) => renderChoicePill(option, true, false, false)}
    />
  );
};

const ChoiceSelectInline: FunctionComponent<ChoiceSelectProps> = ({
  isMultipleSelect,
  columnCount,
  options,
  value,
  name,
  onChange = noop,
  disabled,
  readOnly,
  required,
}) => {
  const radioGroupId = useId();
  const classes = cx("choice-select-inline", {
    "one-column": columnCount === 1,
    "two-columns": columnCount === 2,
    "three-columns": columnCount === 3,
  });

  // toMultipleValue, not a bare cast: ?? only guards null and undefined, so a scalar left by a
  // restored checkpoint or a prefill reached .some/.filter and threw (FORMS-WEB-X1). The owning
  // control repairs the stored value, but this component also takes `value` as a plain prop.
  const multipleValue = toMultipleValue<FieldChoiceValue>(value);

  const isOtherSelected = isMultipleSelect
    ? multipleValue.some((v) => String(v) === OTHER_OPTION_VALUE)
    : String(value) === OTHER_OPTION_VALUE;

  const isInteractionDisabled = Boolean(disabled) || Boolean(readOnly);

  const handleChange = useCallback(
    (optionValue: FieldChoiceValue) => {
      if (isInteractionDisabled) return;
      if (isMultipleSelect) {
        const next = multipleValue.some((v) => String(v) === String(optionValue))
          ? multipleValue.filter((v) => String(v) !== String(optionValue))
          : [...multipleValue, optionValue];
        onChange(next);
      } else {
        const isSameOption = String(value) === String(optionValue);
        if (isSameOption) {
          if (!required) {
            onChange(undefined as unknown as FieldChoiceValue);
          }
        } else {
          onChange(optionValue);
        }
      }
    },
    [isInteractionDisabled, isMultipleSelect, multipleValue, onChange, required, value],
  );

  const renderOptionButton = (option: FieldChoice): JSX.Element => {
    const optionValue = String(option.value);
    const isSelected = isMultipleSelect
      ? multipleValue.some((v) => String(v) === optionValue)
      : optionValue === value;

    return (
      <button
        type="button"
        key={optionValue}
        aria-label={option.label}
        aria-pressed={isSelected}
        disabled={isInteractionDisabled}
        className={cx("choice-select-inline__item", {
          "is-selected": isSelected,
          "choice-select-inline__item--disabled": isInteractionDisabled,
        })}
        onClick={() => {
          if (isInteractionDisabled) return;
          handleChange(option.value);
        }}
      >
        {isMultipleSelect ? (
          <Checkbox
            name={radioGroupId}
            className="choice-select-inline__checkbox"
            checked={isSelected}
            onChange={noop}
          />
        ) : (
          <RadioButton
            className="choice-select-inline__button"
            name={radioGroupId}
            value={optionValue}
            checked={isSelected}
            onChange={noop}
          />
        )}
        {renderChoicePill(option, false, true, false)}
      </button>
    );
  };

  return (
    <>
      <div className={classes}>{options.map((option) => renderOptionButton(option))}</div>
      {isOtherSelected && name && <OtherOptionField name={`metadata_${name}`} />}
    </>
  );
};

export const ChoiceSelect: React.FC<ChoiceSelectProps> = (props) => {
  if (props.type === "inline") {
    return <ChoiceSelectInline {...props} />;
  }

  return <ChoiceSelectDropdown {...props} />;
};
